// Shot.js
// Libraries
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';

// Components
import ExpandedForm from '../Elements/ShotCard/ExpandedForm.js';
import GeneralModal from '../Modals/GeneralModal.js';
import ImageLightboxModal from '../Modals/ImageLightboxModal.js';
import Icon from '../Elements/Icon';
import { MuiCreatableSelect } from '../Elements/ShotCard/MuiCreatableSelect.js';

// Field Components
import SubjectsField from './Project/subjectField.js';
import DescriptionField from './Project/descriptionField.js';
import ProFlag from '../Elements/ProFlag.js';
import ImageUpload from '../Elements/ShotCard/ImageUpload.js';

// Context
import { UserContext } from '../../context/User';
import ScriptContext from '../../context/Script';
import SessionContext from '../../context/Session';
import ProjectViewContext from '../../context/ProjectView';

// Services
import dictionaryService from '../../services/dictionaryService.js';
import scriptService from '../../services/scriptService.js';
import shotService from '../../services/shotService.js';
import utils from '../../services/utilities.js';
import useScenePermissions from '../../hooks/useScenePermissions.js';

// MUI
import { Box, Paper, Stack, Divider, IconButton, Tooltip, Popover, Button } from '@mui/material';

// MUI Icons
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

const ACTION_RAIL_WIDTH = 46;
const SHOT_TYPE_WIDTH = 130;
const HEADER_CONTROL_HEIGHT = 44;
const IMAGE_BOX_SIZE = SHOT_TYPE_WIDTH;
const HEADER_GAP = 1.5; // MUI spacing units

const RAIL_BTN = 30;
const RAIL_ICON = 18;
const RAIL_GAP = 1;

const RAIL_BTN_COMPACT = 22;
const RAIL_ICON_COMPACT = 16;
const RAIL_GAP_COMPACT = 0.5;

const Shot = ({
  shot,
  isSelected,
  updateShot,
  scene,
  dictionaries,
  updateDictionaries,
  onConfirmDelete,
  addShot,
}) => {
  const { user } = useContext(UserContext);
  const { script, setScript } = useContext(ScriptContext);
  const { generalModalDetails, setGeneralModalDetails } = useContext(SessionContext);
  const { view, lockedFields, lockField, unlockField } = useContext(ProjectViewContext);

  const componentRef = useRef(null);
  const paperRef = useRef(null);
  const trashBtnRef = useRef(null);
  const cancelBtnRef = useRef(null);
  const [compactRail, setCompactRail] = useState(false);
  const [deleteAnchorEl, setDeleteAnchorEl] = useState(null);
  const [isExiting, setIsExiting] = useState(false);
  const [lightboxShotId, setLightboxShotId] = useState(null);

  const { isReadOnly, isSceneLocked, isGenerationLocked } = useScenePermissions(scene, script);
  const locked = isReadOnly || isSceneLocked || isGenerationLocked;
  const shotTypeLocked = locked || lockedFields?.has?.(`shot-${shot.id}-shot_type1`);
  const subjectsLocked = locked || lockedFields?.has?.(`shot-${shot.id}-subjects`);

  const isSingleColumnView = Number(view?.viewColumns) === 1;
  const showImages = !!view?.includeImages;
  const canUseImages = !!view?.sceneImagesAllowed && !isSceneLocked;

  const imagesViewable = isSingleColumnView && showImages;

  const tooltipSx = {
    maxWidth: 240,
    whiteSpace: 'normal',
    wordBreak: 'normal',
    overflowWrap: 'break-word',
  };

  // --- Auto-compact the right rail if the card is short ---
  useEffect(() => {
    const el = paperRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;

    const ro = new ResizeObserver((entries) => {
      const h = entries?.[0]?.contentRect?.height ?? 0;

      // If the card is short, shrink the rail controls so they fit
      const next = h > 0 && h < 160;
      setCompactRail(next);
    });

    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const railBtn = compactRail ? RAIL_BTN_COMPACT : RAIL_BTN;
  const railIconFontSize = compactRail ? 'small' : 'small';
  const railTrashSize = compactRail ? RAIL_ICON_COMPACT : RAIL_ICON;
  const railGap = compactRail ? RAIL_GAP_COMPACT : RAIL_GAP;

  const expandContractShot = () => {
    shot.expanded = !shot.expanded;
    updateShot(scene.id, shot, 'expanded');
  };

  const closeDeletePopover = () => {
    setDeleteAnchorEl(null);
    trashBtnRef.current?.focus();
  };

  const handleConfirmDelete = () => {
    setDeleteAnchorEl(null);
    setIsExiting(true);
    onConfirmDelete(scene.id, shot.id);
  };

  const addShotTypeOption = async (optionObj) => {
    const trimmed = String(optionObj?.value || optionObj || '').trim();
    if (!trimmed) return null;
    const newEntry = await dictionaryService.addUserDictionaryEntry(
      user.id,
      'shottype',
      trimmed,
      shot.id,
    );
    if (newEntry) {
      updateDictionaries((prev) => ({
        ...prev,
        types: [...(prev.types || []), trimmed],
      }));
    }
    return { value: trimmed, label: trimmed };
  };

  const addNewSpecialEquipment = async (optionObj) => {
    const trimmed = String(optionObj?.value || optionObj || '').trim();
    if (!trimmed) return null;

    const alreadyKnown = (dictionaries?.special_equipment || []).some(
      (v) => String(v).trim().toLowerCase() === trimmed.toLowerCase(),
    );
    if (!alreadyKnown) {
      const newEntry = await dictionaryService.addDictionaryEntry(
        'special_equipment',
        trimmed,
        'equipment',
        script.id,
      );
      if (newEntry) {
        updateDictionaries((prev) => ({
          ...prev,
          special_equipment: [...(prev.special_equipment || []), trimmed],
        }));
      }
    }
    return { value: trimmed, label: trimmed };
  };

  const addNewUserDictionary = async (value, dictionary) => {
    value = value && value.value ? value.value : value;
    const newEntry = await dictionaryService.addUserDictionaryEntry(
      user.id,
      dictionary,
      value,
      shot.id,
    );
    if (newEntry) {
      updateDictionaries((prev) => ({
        ...prev,
        [dictionary]: [...(prev[dictionary] || []), newEntry],
      }));
      return newEntry;
    }
  };

  const handleShotFieldChange = async (shotId, field, update) => {
    try {
      let workingShot = shot;
      let fullReplace = false;

      if (shotId === 0) {
        const newShot = await shotService.addShot(scene.id, 1);
        // The server response has no `expanded` flag -- it's UI-only state,
        // not persisted -- so carry over the placeholder's value or this
        // full-replace collapses the card on its very first edit.
        workingShot = { ...newShot, expanded: shot.expanded };
        shotId = workingShot.id;
        shot.id = shotId;
        fullReplace = true;
      } else if (!shotId) {
        shotId = shot.id;
      }

      let sanitizedUpdate = update;

      // If it is a dictionary update, strip out the label and only send the value
      if (update && update[0] && update[0].value) {
        sanitizedUpdate = update ? update.map((item) => item.value) : [];
        const userDictionaries = ['movement', 'camera', 'focus', 'frame', 'focal'];
        if (userDictionaries.includes(field)) {
          update = sanitizedUpdate;
        }
      }

      workingShot[field] = update;

      if (field === 'subject') {
        workingShot[field] = (update || []).map((item) => ({ subject_id: item }));
      }

      // Commit to local state before the network round-trip so the controlled
      // multi-select's value reflects each click immediately. Otherwise rapid
      // consecutive selections race against the save and clobber each other,
      // since each new selection is computed from the (still stale) previous value.
      if (!fullReplace) {
        updateShot(scene.id, workingShot, field);
      } else {
        updateShot(scene.id, workingShot, 'full', 0);
      }

      await scriptService.changeShotChange(shotId, field, sanitizedUpdate, scene.id);
    } catch (error) {
      console.error(error);
    }

    window.scrollTo(0, window.scrollY);
  };

  const uploadAttachment = async (file) => {
    const attachment = await shotService.uploadAttachment(script.script_path, shot.id, file);

    if (attachment?.error) {
      setGeneralModalDetails({
        open: true,
        header: <span>Uh oh!</span>,
        message: (
          <div>
            Upload failed. We accept image files of 2MB or less- JPG, JPEG, or PNG.
            <br />
            <br />
            Please try again or{' '}
            <a href="https://www.shotkraft.com/help" target="_blank" rel="noreferrer">
              Contact Us
            </a>{' '}
            for help.
            <br />
            <br />
            <div className="fullWidthContainer">
              <span className="OneHalfFlex"></span>
              <button
                className="buttonGray OneHalfFlex"
                onClick={() => setGeneralModalDetails({ open: false })}
              >
                Cancel
              </button>
            </div>
          </div>
        ),
      });
    } else if (attachment) {
      shot.image = attachment;
      updateShot(scene.id, shot, 'image');
    }
  };

  const retrieveImg = async () => {
    shot.image = await shotService.retrieveImg(shot.id);
    updateShot(scene.id, shot, 'image');
  };

  const deleteImg = async () => {
    const result = await shotService.deleteShotImgs(shot.id);
    if (result) {
      shot.image = null;
      updateShot(scene.id, shot, 'image');
    } else {
      setGeneralModalDetails({
        open: true,
        header: <span>Uh oh!</span>,
        message: (
          <div>
            We encountered an error deleting this attachment. Please try again or{' '}
            <a href="https://www.shotkraft.com/help" target="_blank" rel="noreferrer">
              Contact Us
            </a>
            .
            <br />
            <br />
            <div className="fullWidthContainer">
              <span className="OneHalfFlex"></span>
              <button
                className="buttonGray OneHalfFlex"
                onClick={() => setGeneralModalDetails({ open: false })}
              >
                Cancel
              </button>
            </div>
          </div>
        ),
      });
    }
  };

  // Retrieve image when attachments exist and images are enabled
  useEffect(() => {
    if (showImages && shot?.attachments?.length > 0) {
      retrieveImg?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showImages, shot?.attachments]);

  const shotNumberLabel = useMemo(() => {
    const sn = scene?.scene_number ? `${scene.scene_number}.` : '';
    const sh = shot?.shot_number ?? '';
    return `${sn}${sh}`;
  }, [scene?.scene_number, shot?.shot_number]);

  const shotTypeOptions = useMemo(() => {
    const list = Array.isArray(dictionaries?.types) ? dictionaries.types : [];
    return list.filter(Boolean).map((t) => String(t));
  }, [dictionaries?.types]);

  const specialEquipmentOptions = useMemo(() => {
    const list = Array.isArray(dictionaries?.special_equipment)
      ? dictionaries.special_equipment
      : [];
    return list.filter(Boolean).map((t) => String(t));
  }, [dictionaries?.special_equipment]);

  const subjectsFieldId = `shot-${shot.id}-subjects-input`;
  const focusSubjects = () => document.getElementById(subjectsFieldId)?.focus();

  // Sibling shots within this scene that currently have an image, used to
  // drive left/right navigation inside the lightbox.
  const imageShots = useMemo(
    () => (scene?.shot_list || []).filter((s) => !!s?.image),
    [scene?.shot_list],
  );

  const lightboxIndex = useMemo(() => {
    if (lightboxShotId == null) return null;
    const idx = imageShots.findIndex((s) => s.id === lightboxShotId);
    return idx === -1 ? null : idx;
  }, [lightboxShotId, imageShots]);

  const downloadShotImage = (targetShot) => {
    const filename = utils.buildShotImageFilename(scene?.scene_number, targetShot?.shot_number);
    utils.downloadFile(targetShot?.image, filename);
  };

  const imageBox = (
    <Box
      sx={{
        width: IMAGE_BOX_SIZE,
        minWidth: IMAGE_BOX_SIZE,
        minHeight: IMAGE_BOX_SIZE,
        border: '1px dashed',
        borderColor: 'divider',
        borderRadius: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.paper',
        padding: 0,
      }}
    >
      {canUseImages ? (
        <ImageUpload
          uploadAttachment={uploadAttachment}
          deleteAttachment={deleteImg}
          image={shot?.image || null}
          isLoading={shot?.imageIsLoading || false}
          readOnly={isReadOnly}
          showError={
            shot?.imageIsLoading && !(shot?.shot_description || '').trim() && !shot?.image
              ? 'Cannot generate an image without a shot description.'
              : shot?.imageGenerationBlocked
                ? "This shot's description can't be used to generate an image. Upload your own image instead."
                : shot?.imageGenerationFailed
                  ? "Image generation didn't finish. Try again."
                  : null
          }
          onRetry={
            shot?.imageGenerationFailed && !isReadOnly
              ? () => {
                  scriptService
                    .generateReferenceImages(script.id, scene.id)
                    .catch((error) => console.error('Error retrying image generation:', error));
                }
              : null
          }
          onView={() => setLightboxShotId(shot.id)}
          onDownload={() => downloadShotImage(shot)}
        />
      ) : (
        <Stack spacing={1} alignItems="center">
          <ProFlag
            type="pro"
            scriptLevel={script ? script.features : 'Basic'}
            scriptId={script?.id}
            handleUpgrade={() =>
              setScript?.((prev) => ({
                ...prev,
                features: 'Pro',
              }))
            }
          />
          <Box sx={{ fontSize: 14, color: 'text.secondary' }}>Image</Box>
        </Stack>
      )}
    </Box>
  );

  const shotTypeSelect = (
    <MuiCreatableSelect
      singleSelect
      id={`shot-${shot.id}-shot_type1`}
      optionList={shotTypeOptions}
      selected={shot?.shot_type1 ?? ''}
      baseItem={shot}
      selectedFieldName="shot_type1"
      changeHandler={handleShotFieldChange}
      addOption={!isReadOnly ? addShotTypeOption : null}
      placeholder="Type"
      readOnly={shotTypeLocked}
      lockField={lockField}
      unlockField={unlockField}
      onAfterChange={focusSubjects}
      sx={{
        width: SHOT_TYPE_WIDTH,
        minWidth: SHOT_TYPE_WIDTH,
        flex: '0 0 auto',
        '& .MuiOutlinedInput-root': {
          height: HEADER_CONTROL_HEIGHT,
          backgroundColor: '#F3E8FA',
          padding: '0 !important',
          paddingRight: '30px !important',
        },
        '& .MuiAutocomplete-input': {
          paddingTop: '0 !important',
          paddingBottom: '0 !important',
          paddingLeft: '14px !important',
        },
      }}
    />
  );

  const descriptionMatchImageSx = {
    bgcolor: 'background.paper',
    '& .MuiOutlinedInput-root': {
      minHeight: IMAGE_BOX_SIZE,
      alignItems: 'stretch',
    },
    '& .MuiInputLabel-root': {
      bgcolor: 'background.paper',
      px: 0.5,
    },
  };

  // ---------- HEADER (single vs multi) ----------
  const singleHeaderNoImage = (
    <Stack
      direction="row"
      spacing={HEADER_GAP}
      alignItems="flex-start"
      sx={{
        minWidth: 0,
        width: '100%',
        flexWrap: { xs: 'wrap', md: 'nowrap' },
        rowGap: 1.25,
        overflow: 'hidden',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <span className="shotHeaderNumber">{shotNumberLabel}</span>
      </Box>

      {shotTypeSelect}

      <Box sx={{ flex: '1 1 0', minWidth: 160, maxWidth: '100%' }}>
        <SubjectsField
          shot={shot}
          scene={scene}
          dictionaries={dictionaries}
          updateDictionaries={updateDictionaries}
          handleShotFieldChange={handleShotFieldChange}
          locked={subjectsLocked}
          lockKey={`shot-${shot.id}-subjects`}
          id={subjectsFieldId}
          sx={{ '& .MuiOutlinedInput-root': { minHeight: HEADER_CONTROL_HEIGHT } }}
        />
      </Box>

      <Box
        sx={{
          flex: { xs: '1 1 100%', md: '1 1 50%' },
          minWidth: 0,
          maxWidth: { xs: '100%', md: '50%' },
          overflow: 'hidden',
        }}
      >
        <DescriptionField
          shot={shot}
          handleShotFieldChange={handleShotFieldChange}
          script={script}
          readOnly={locked}
          lockKey={`shot-${shot.id}-shot_description`}
          placeholder="Describe the shot…"
          sx={{ bgcolor: 'background.paper' }}
        />
      </Box>
    </Stack>
  );

  const singleHeaderImagesViewableLayout = (
    <Stack
      direction="row"
      spacing={HEADER_GAP}
      alignItems="flex-start"
      sx={{
        width: '100%',
        minWidth: 0,
        flexWrap: { xs: 'wrap', md: 'nowrap' },
        rowGap: 2,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <span className="shotHeaderNumber">{shotNumberLabel}</span>
      </Box>

      <Box sx={{ width: SHOT_TYPE_WIDTH, minWidth: SHOT_TYPE_WIDTH, flex: '0 0 auto' }}>
        <Stack spacing={1.25} sx={{ width: '100%' }}>
          {shotTypeSelect}
          {imageBox}
        </Stack>
      </Box>

      <Box sx={{ flex: '1 1 0', minWidth: 0, maxWidth: '100%', overflow: 'hidden' }}>
        <Stack spacing={1.5} sx={{ width: '100%' }}>
          <SubjectsField
            shot={shot}
            scene={scene}
            dictionaries={dictionaries}
            updateDictionaries={updateDictionaries}
            handleShotFieldChange={handleShotFieldChange}
            locked={subjectsLocked}
            lockKey={`shot-${shot.id}-subjects`}
            sx={{ '& .MuiOutlinedInput-root': { minHeight: HEADER_CONTROL_HEIGHT } }}
          />

          <DescriptionField
            shot={shot}
            handleShotFieldChange={handleShotFieldChange}
            script={script}
            readOnly={locked}
            lockKey={`shot-${shot.id}-shot_description`}
            placeholder="Describe the shot…"
            minRows={1}
            sx={descriptionMatchImageSx}
          />
        </Stack>
      </Box>
    </Stack>
  );

  const multiHeader = (
    <Stack
      direction="row"
      alignItems="flex-start"
      spacing={HEADER_GAP}
      sx={{
        minWidth: 0,
        width: '100%',
        flexWrap: { xs: 'wrap', md: 'nowrap' },
        rowGap: 1.25,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <span className="shotHeaderNumber">{shotNumberLabel}</span>
      </Box>

      {shotTypeSelect}

      <Box sx={{ flex: '1 1 0', minWidth: 160, maxWidth: '100%' }}>
        <SubjectsField
          shot={shot}
          scene={scene}
          dictionaries={dictionaries}
          updateDictionaries={updateDictionaries}
          handleShotFieldChange={handleShotFieldChange}
          locked={subjectsLocked}
          lockKey={`shot-${shot.id}-subjects`}
          id={subjectsFieldId}
          sx={{ '& .MuiOutlinedInput-root': { minHeight: HEADER_CONTROL_HEIGHT } }}
        />
      </Box>
    </Stack>
  );

  const descriptionFullWidth = (
    <DescriptionField
      shot={shot}
      handleShotFieldChange={handleShotFieldChange}
      script={script}
      readOnly={locked}
      lockKey={`shot-${shot.id}-shot_description`}
      minRows={1}
      sx={{ bgcolor: 'background.paper' }}
    />
  );

  const descriptionWithImageSideBySide = (
    <Stack
      direction={{ xs: 'column', md: 'row' }}
      spacing={2}
      alignItems="stretch"
      sx={{ width: '100%' }}
    >
      {/* Image */}
      <Box sx={{ display: 'flex', alignItems: 'stretch' }}>{imageBox}</Box>

      {/* Description: match image height on md+ when short, grow for longer text */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          minWidth: 0,
          minHeight: { xs: 'auto', md: IMAGE_BOX_SIZE },
        }}
      >
        <Box sx={{ width: '100%', minHeight: { xs: 'auto', md: '100%' } }}>
          <DescriptionField
            shot={shot}
            handleShotFieldChange={handleShotFieldChange}
            script={script}
            readOnly={locked}
            lockKey={`shot-${shot.id}-shot_description`}
            minRows={1}
            sx={{
              bgcolor: 'background.paper',

              '& .MuiOutlinedInput-root': {
                minHeight: { xs: 'auto', md: '100%' },
                alignItems: 'stretch',
              },

              '& .MuiInputLabel-root': {
                bgcolor: 'background.paper',
                px: 0.5,
              },
            }}
          />
        </Box>
      </Box>
    </Stack>
  );

  return (
    <Box
      ref={componentRef}
      sx={{
        width: '100%',
        marginBottom: 3,
        px: 0.5,
        transition: 'opacity 0.2s ease, transform 0.2s ease',
        opacity: isExiting ? 0 : 1,
        transform: isExiting ? 'scale(0.95)' : 'scale(1)',
      }}
    >
      {generalModalDetails && (
        <GeneralModal
          generalModalDetails={generalModalDetails}
          setModalIsOpen={setGeneralModalDetails}
        />
      )}

      <ImageLightboxModal
        shots={imageShots}
        sceneNumber={scene?.scene_number}
        currentIndex={lightboxIndex}
        onNavigate={(idx) => setLightboxShotId(imageShots[idx]?.id ?? null)}
        onClose={() => setLightboxShotId(null)}
      />

      <Paper
        ref={paperRef}
        variant="outlined"
        sx={{
          pt: 2.5,
          pb: 2.5,
          px: 2,
          borderRadius: 3,
          bgcolor: '#F9FAFC',
          borderColor: isSelected ? 'primary.main' : 'divider',
          boxShadow: 'none',
          position: 'relative',
          pr: script && !isReadOnly && !isGenerationLocked ? `${ACTION_RAIL_WIDTH + 8}px` : 2,
        }}
      >
        {/* Side action rail (Add / Delete / Expand) — Delete/Add hidden for locked scenes, Expand stays available */}
        {script && !isReadOnly && !isGenerationLocked && (
          <Box
            sx={{
              position: 'absolute',
              top: 4,
              right: 4,
              width: `${ACTION_RAIL_WIDTH}px`,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: railGap,
              zIndex: 2,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {!isSceneLocked && (
              <>
                <Tooltip
                  title="Delete shot"
                  placement="left"
                  slotProps={{ tooltip: { sx: tooltipSx } }}
                >
                  <Box
                    ref={trashBtnRef}
                    role="button"
                    tabIndex={0}
                    aria-label={`Delete shot ${shotNumberLabel}`}
                    sx={{
                      width: railBtn,
                      height: railBtn,
                      display: 'grid',
                      placeItems: 'center',
                      borderRadius: 2,
                      cursor: 'pointer',
                      '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' },
                    }}
                    onClick={(e) => setDeleteAnchorEl(e.currentTarget)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setDeleteAnchorEl(e.currentTarget);
                      }
                    }}
                  >
                    <Icon icon="Trash" size={railTrashSize} color="Orange" />
                  </Box>
                </Tooltip>

                <Popover
                  open={Boolean(deleteAnchorEl)}
                  anchorEl={deleteAnchorEl}
                  onClose={closeDeletePopover}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                  transitionDuration={150}
                  TransitionProps={{ onEntered: () => cancelBtnRef.current?.focus() }}
                  slotProps={{
                    paper: {
                      role: 'dialog',
                      'aria-label': 'Confirm delete',
                      'aria-modal': true,
                      sx: {
                        width: 320,
                        p: 2,
                        mt: '4px',
                        borderRadius: 2,
                        border: '0.5px solid',
                        borderColor: 'divider',
                        boxShadow: '0 4px 16px rgba(0,0,0,0.14)',
                      },
                    },
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 1.25 }}>
                    <Icon icon="Trash" color="Orange" size={18} />
                    <Box sx={{ fontSize: 15, fontWeight: 700, color: '#212121' }}>Delete Shot</Box>
                  </Stack>
                  <Divider sx={{ mb: 1.25 }} />
                  <Box sx={{ fontSize: 13, color: '#212121', lineHeight: 1.5, mb: 1.5 }}>
                    Are you sure you want to delete this shot? This action cannot be undone.
                  </Box>
                  <Stack direction="row" spacing={2} alignItems="center">
                    {/* Explicit bg/border/active overrides: the global MuiButton theme
                        forces a purple background and border: none on every button. */}
                    <Button
                      ref={cancelBtnRef}
                      size="small"
                      variant="outlined"
                      disableElevation
                      disableFocusRipple
                      onClick={closeDeletePopover}
                      sx={{
                        flex: 1,
                        minHeight: 36,
                        borderRadius: '4px',
                        textTransform: 'none',
                        fontSize: 13,
                        color: '#212121',
                        bgcolor: '#FFFFFF',
                        border: '1px solid #E0E0E0',
                        '&:hover': { bgcolor: '#F5F5F5', border: '1px solid #E0E0E0' },
                        '&:active': { bgcolor: '#EEEEEE' },
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      disableElevation
                      onClick={handleConfirmDelete}
                      sx={{
                        flex: 1,
                        minHeight: 36,
                        borderRadius: '4px',
                        textTransform: 'none',
                        fontSize: 13,
                        color: '#FFFFFF',
                        bgcolor: '#CD5C4C',
                        '&:hover': { bgcolor: '#B84B3C' },
                        '&:active': { bgcolor: '#A84334' },
                      }}
                    >
                      Delete Shot
                    </Button>
                  </Stack>
                </Popover>

                <Tooltip
                  title="Add shot"
                  placement="left"
                  slotProps={{ tooltip: { sx: tooltipSx } }}
                >
                  <IconButton
                    size="small"
                    onClick={() => addShot(shot.id)}
                    sx={{ width: railBtn, height: railBtn, borderRadius: 1 }}
                  >
                    <AddIcon fontSize={railIconFontSize} />
                  </IconButton>
                </Tooltip>
              </>
            )}

            <Tooltip
              title={shot?.expanded ? 'Collapse' : 'Expand'}
              placement="left"
              slotProps={{ tooltip: { sx: tooltipSx } }}
            >
              <IconButton
                size="small"
                onClick={expandContractShot}
                sx={{ width: railBtn, height: railBtn, borderRadius: 2 }}
              >
                {shot?.expanded ? (
                  <ExpandLessIcon fontSize={railIconFontSize} />
                ) : (
                  <ExpandMoreIcon fontSize={railIconFontSize} />
                )}
              </IconButton>
            </Tooltip>
          </Box>
        )}

        {isSingleColumnView ? (
          <>
            {imagesViewable ? singleHeaderImagesViewableLayout : singleHeaderNoImage}

            <Stack spacing={2}>
              <ExpandedForm
                scene={scene}
                shot={shot}
                dictionaries={dictionaries}
                handleShotFieldChange={handleShotFieldChange}
                addNewUserDictionary={addNewUserDictionary}
                specialEquipmentOptions={specialEquipmentOptions}
                addNewSpecialEquipment={addNewSpecialEquipment}
              />
            </Stack>
          </>
        ) : (
          <>
            {multiHeader}

            <Divider sx={{ my: 1 }} />

            {showImages ? descriptionWithImageSideBySide : descriptionFullWidth}

            <Stack spacing={2}>
              <ExpandedForm
                scene={scene}
                shot={shot}
                dictionaries={dictionaries}
                handleShotFieldChange={handleShotFieldChange}
                addNewUserDictionary={addNewUserDictionary}
                specialEquipmentOptions={specialEquipmentOptions}
                addNewSpecialEquipment={addNewSpecialEquipment}
              />
            </Stack>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default Shot;
